import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Grid, Paper, List, ListItem, ListItemText, CircularProgress } from '@mui/material';
import { philippineLocations } from '../../utils/philippineLocations';

// Add type declaration for Google Maps auth failure callback
declare global {
  interface Window {
    google?: any;
    gm_authFailure?: () => void;
  }
}

// Define interfaces
interface Patient {
  id: string;
  name: string;
  municipality: string;
  province: string;
  // other properties...
}

interface GeospatialMapProps {
  patientData: Patient[];
}

// ========================================================
// SET YOUR GOOGLE MAPS API KEY HERE
// ========================================================
const GOOGLE_MAPS_API_KEY = 'AIzaSyB32lqRecf0hjxmUJhI54afkTNY4I1C0nk';
// ========================================================

// In development environments, you can use a development-friendly approach
// For production, you should create a properly secured API key
const isDevelopment = false;//process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';

// Only use fallback mode if no API key is provided
const useFallbackMap = GOOGLE_MAPS_API_KEY === 'AIzaSyB32lqRecf0hjxmUJhI54afkTNY4I1C0nk' || GOOGLE_MAPS_API_KEY === '';

// Global variable to track if we've already started loading Google Maps
let isGoogleMapsLoading = false;
 

const GeospatialMap: React.FC<GeospatialMapProps> = ({ patientData }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [selectedProvince, setSelectedProvince] = useState<string>('');
  const [selectedMunicipality, setSelectedMunicipality] = useState<string>('');
  const [locationStats, setLocationStats] = useState<{[key: string]: number}>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error] = useState<string | null>(null);
  const [fallbackMode, setFallbackMode] = useState(false);
  
  // Extract unique provinces and municipalities from patient data
  const provinces = useMemo(() => {
    return Array.from(new Set(patientData.map(patient => patient.province)));
  }, [patientData]);
  
  const filteredMunicipalities = useMemo(() => {
    if (!selectedProvince) return [];
    return Array.from(new Set(
      patientData
        .filter(patient => patient.province === selectedProvince)
        .map(patient => patient.municipality)
    ));
  }, [patientData, selectedProvince]);

  // Calculate location statistics from the filtered data
  useEffect(() => {
    // Filter patient data based on selections
    let filteredData = patientData;
    
    if (selectedProvince) {
      filteredData = filteredData.filter(patient => patient.province === selectedProvince);
    }
    
    if (selectedMunicipality) {
      filteredData = filteredData.filter(patient => patient.municipality === selectedMunicipality);
    }
    
    // Count patients by location (municipality)
    const stats: {[key: string]: number} = {};
    filteredData.forEach(patient => {
      const location = patient.municipality;
      if (stats[location]) {
        stats[location]++;
      } else {
        stats[location] = 1;
      }
    });
    
    setLocationStats(stats);
  }, [patientData, selectedProvince, selectedMunicipality]);

  // Load Google Maps API
  useEffect(() => {
    // If Google Maps is already loaded, don't load it again
    if (window.google && window.google.maps) {
      setIsLoading(false);
      return;
    }

    // If loading has already started, wait for it to complete
    if (isGoogleMapsLoading) {
      const checkGoogleMapsLoaded = setInterval(() => {
        if (window.google && window.google.maps) {
          setIsLoading(false);
          clearInterval(checkGoogleMapsLoaded);
        }
      }, 100);
      
      return () => clearInterval(checkGoogleMapsLoaded);
    }

    // Set loading started flag
    isGoogleMapsLoading = true;

    // Remove any existing Google Maps scripts
    const existingScripts = document.querySelectorAll('script[src*="maps.googleapis.com/maps/api"]');
    existingScripts.forEach(script => script.remove());

    try {
      // Create script element - use development mode if in development environment
      const script = document.createElement('script');
      
      if (isDevelopment && useFallbackMap) {
        // For development, use the fallback approach
        setFallbackMode(true);
        setIsLoading(false);
        isGoogleMapsLoading = false;
        return;
      } else {
        // Normal loading with API key
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=visualization`;
        script.async = true;
        script.defer = true;
      }
      
      script.onload = () => {
        setIsLoading(false);
      };
      
      script.onerror = () => {
        console.error('Failed to load Google Maps API. Falling back to simplified view.');
        setFallbackMode(true);
        isGoogleMapsLoading = false;
        setIsLoading(false);
      };
      
      document.head.appendChild(script);
      
      // Add error handler for API key issues
      window.gm_authFailure = () => {
        console.error('Google Maps authentication failed. Falling back to simplified view.');
        setFallbackMode(true);
        setIsLoading(false);
      };
    } catch (err) {
      console.error('Error setting up Google Maps:', err);
      setFallbackMode(true);
      setIsLoading(false);
    }
  }, []);

  // Initialize Google Maps once the API is loaded
  useEffect(() => {
    if (fallbackMode || isLoading || !mapRef.current || map) return;
    
    try {
      const mapOptions = {
        center: { lat: 12.8797, lng: 121.7740 }, // Center of Philippines
        zoom: 6,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true
      };
      
      const newMap = new google.maps.Map(mapRef.current, mapOptions);
      setMap(newMap);
      
      // Group patients by location to count cases per area (for map visualization)
      const locationCounts: {[key: string]: {count: number, position: {lat: number, lng: number}}} = {};
      
      patientData.forEach(patient => {
        const location = patient.municipality;
        const coordinates = philippineLocations[patient.municipality as keyof typeof philippineLocations] || 
                          philippineLocations[patient.province as keyof typeof philippineLocations];
        
        if (coordinates) {
          if (locationCounts[location]) {
            locationCounts[location].count++;
          } else {
            locationCounts[location] = {
              count: 1,
              position: coordinates
            };
          }
        }
      });
      
      // Find the maximum count to normalize the heat intensity
      const counts = Object.values(locationCounts).map(loc => loc.count);
      const maxCount = Math.max(...counts);
      
      // Create heatmap data points with weighted values
      const heatmapData = Object.entries(locationCounts).map(([location, data]) => {
        // Normalize count to a value between 0 and 1
        const normalizedWeight = data.count / maxCount;
        return {
          location: new google.maps.LatLng(data.position.lat, data.position.lng),
          weight: normalizedWeight
        };
      });
      
      // Create the heatmap layer
      const heatmap = new google.maps.visualization.HeatmapLayer({
        map: newMap,
        radius: 30,
        opacity: 0.8,
        gradient: [
          'rgba(255, 165, 0, 0)', // Orange with 0 opacity (for areas with no cases)
          'rgba(255, 165, 0, 1)', // Orange (lowest)
          'rgba(255, 140, 0, 1)', // Dark Orange
          'rgba(255, 100, 0, 1)', // Orange-Red
          'rgba(255, 50, 0, 1)',  // Bright Red
          'rgba(255, 0, 0, 1)'    // Pure Red (highest)
        ]
      });
      
      // Create proper weighted data for the heatmap
      const weightedData = heatmapData.map(point => ({
        location: point.location,
        weight: point.weight * 10
      })) as google.maps.visualization.WeightedLocation[];
      
      heatmap.setData(weightedData);
      
      // Save the heatmap reference for cleanup
      heatmapRef.current = heatmap;
      
    } catch (err) {
      console.error("Error initializing map:", err);
      setFallbackMode(true);
    }
  }, [isLoading, fallbackMode, map, patientData, locationStats]);

  // Initialize a ref to store the current heatmap instance
  const heatmapRef = useRef<google.maps.visualization.HeatmapLayer | null>(null);

  // Update map when filters change
  useEffect(() => {
    if (!map || fallbackMode) return;
    
    // Filter patient data based on selections
    let filteredData = patientData;
    
    if (selectedProvince) {
      filteredData = filteredData.filter(patient => patient.province === selectedProvince);
    }
    
    if (selectedMunicipality) {
      filteredData = filteredData.filter(patient => patient.municipality === selectedMunicipality);
    }
    
    // Group patients by location to count cases per area (for map visualization)
    const locationCounts: {[key: string]: {count: number, position: {lat: number, lng: number}}} = {};
    
    filteredData.forEach(patient => {
      const location = patient.municipality;
      const coordinates = philippineLocations[patient.municipality as keyof typeof philippineLocations] || 
                        philippineLocations[patient.province as keyof typeof philippineLocations];
      
      if (coordinates) {
        if (locationCounts[location]) {
          locationCounts[location].count++;
        } else {
          locationCounts[location] = {
            count: 1,
            position: coordinates
          };
        }
      }
    });
    
    // Find the maximum count to normalize the heat intensity
    const counts = Object.values(locationCounts).map(loc => loc.count);
    const maxCount = Math.max(...counts, 1); // Ensure we don't divide by zero
    
    // Create heatmap data points with weighted values
    const heatmapData = Object.entries(locationCounts).map(([location, data]) => {
      // Normalize count to a value between 0 and 1
      const normalizedWeight = data.count / maxCount;
      return {
        location: new google.maps.LatLng(data.position.lat, data.position.lng),
        weight: normalizedWeight
      };
    });
    
    // Remove any existing heatmap layer
    if (heatmapRef.current) {
      heatmapRef.current.setMap(null);
    }
    
    // Create new heatmap layer
    const heatmap = new google.maps.visualization.HeatmapLayer({
      map: map,
      radius: 30,
      opacity: 0.8,
      gradient: [
        'rgba(255, 165, 0, 0)', // Orange with 0 opacity (for areas with no cases)
        'rgba(255, 165, 0, 1)', // Orange (lowest)
        'rgba(255, 140, 0, 1)', // Dark Orange
        'rgba(255, 100, 0, 1)', // Orange-Red
        'rgba(255, 50, 0, 1)',  // Bright Red
        'rgba(255, 0, 0, 1)'    // Pure Red (highest)
      ]
    });
    
    // Create proper weighted data for the heatmap
    const weightedData = heatmapData.map(point => ({
      location: point.location,
      weight: point.weight * 10
    })) as google.maps.visualization.WeightedLocation[];
    
    heatmap.setData(weightedData);
    
    // Save the heatmap reference for cleanup
    heatmapRef.current = heatmap;
    
    // Set map center and zoom based on selection
    if (selectedMunicipality && locationCounts[selectedMunicipality]) {
      const pos = locationCounts[selectedMunicipality].position;
      map.setCenter(pos);
      map.setZoom(10);
    } else if (selectedProvince) {
      // Find a center point for the province by averaging coordinates
      const provinceLocations = Object.values(locationCounts);
      if (provinceLocations.length > 0) {
        const latSum = provinceLocations.reduce((sum, loc) => sum + loc.position.lat, 0);
        const lngSum = provinceLocations.reduce((sum, loc) => sum + loc.position.lng, 0);
        const centerLat = latSum / provinceLocations.length;
        const centerLng = lngSum / provinceLocations.length;
        
        map.setCenter({ lat: centerLat, lng: centerLng });
        map.setZoom(8);
      }
    } else {
      map.setCenter({ lat: 12.8797, lng: 121.7740 }); // Center of Philippines
      map.setZoom(6);
    }
  }, [map, fallbackMode, selectedProvince, selectedMunicipality, patientData]);

  const handleProvinceChange = (event: React.ChangeEvent<HTMLInputElement> | any) => {
    const value = event.target.value;
    setSelectedProvince(value);
    setSelectedMunicipality('');
  };

  const handleMunicipalityChange = (event: React.ChangeEvent<HTMLInputElement> | any) => {
    setSelectedMunicipality(event.target.value);
  };

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>
          {error}
        </Typography>
        <Typography variant="body1">
          To fix this issue, you may need to:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="• Check that your Google Maps API key is valid and has the Maps JavaScript API enabled" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Verify that your API key allows usage from your current domain" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Make sure you haven't exceeded usage limits or quotas" />
          </ListItem>
        </List>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="textSecondary">
            For local development, consider using an unrestricted API key (but never use it in production).
          </Typography>
        </Box>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '600px' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading map...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: "100%" }}>
      <Typography variant="h4" gutterBottom>
        Geospatial Distribution
      </Typography>
      <Typography variant="body1" paragraph>
        Geographic distribution of TB cases across the Philippines
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel id="province-select-label">Province</InputLabel>
            <Select
              labelId="province-select-label"
              id="province-select"
              value={selectedProvince}
              label="Province"
              onChange={handleProvinceChange}
            >
              <MenuItem value="">
                <em>All Provinces</em>
              </MenuItem>
              {provinces.map((province) => (
                <MenuItem key={province} value={province}>
                  {province}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel id="municipality-select-label">Municipality/City</InputLabel>
            <Select
              labelId="municipality-select-label"
              id="municipality-select"
              value={selectedMunicipality}
              label="Municipality/City"
              onChange={handleMunicipalityChange}
              disabled={!selectedProvince}
            >
              <MenuItem value="">
                <em>All Municipalities/Cities</em>
              </MenuItem>
              {filteredMunicipalities.map((municipality) => (
                <MenuItem key={municipality} value={municipality}>
                  {municipality}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={8}>
          {fallbackMode ? (
            <Paper sx={{ p: 3, height: 500, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Map Visualization Not Available
              </Typography>
              <Typography variant="body1" align="center">
                To enable the Google Maps visualization:
              </Typography>
              <List sx={{ width: '100%', maxWidth: 500 }}>
                <ListItem>
                  <ListItemText 
                    primary="1. Create a Google Maps API key" 
                    secondary="Visit the Google Cloud Console and create an API key with Maps JavaScript API enabled"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="2. Add your API key to GeospatialMap.tsx" 
                    secondary={`Open GeospatialMap.tsx and replace 'YOUR_API_KEY_HERE' with your actual API key (around line 30)`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="3. Save the file and reload the page" 
                    secondary="The map will automatically load once a valid API key is provided"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="4. Set appropriate restrictions (for production)" 
                    secondary="For production use, restrict your API key to your application's domains in the Google Cloud Console"
                  />
                </ListItem>
              </List>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                The map will work in both development and production once an API key is provided.
              </Typography>
            </Paper>
          ) : (
            <Box
              ref={mapRef}
              sx={{ 
                minHeight: { xs: '300px', sm: '400px', md: '600px' }, // Responsive heights
                width: '100%', 
                border: '1px solid #ddd',
                borderRadius: 1
              }}
            />
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Location Statistics
            </Typography>
            {Object.keys(locationStats).length > 0 ? (
              <List dense>
                {Object.entries(locationStats)
                  .filter(([location]) => {
                    if (selectedMunicipality) return location === selectedMunicipality;
                    if (selectedProvince) {
                      return patientData.some(p => 
                        p.municipality === location && p.province === selectedProvince
                      );
                    }
                    return true;
                  })
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 10)
                  .map(([location, count]) => (
                    <ListItem key={location}>
                      <ListItemText 
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body1">{location}</Typography>
                            <Typography variant="body1" sx={{ 
                              fontWeight: 'bold',
                              color: count >= 6 ? 'error.main' : // Bright red for 6+ cases
                                     count >= 5 ? 'error.light' : // Light red for 5 cases
                                     count >= 4 ? '#ff8c00' : // Dark orange for 4 cases
                                     count >= 3 ? 'warning.main' : // Amber/warning for 3 cases
                                     count >= 2 ? 'warning.light' : // Light amber for 2 cases
                                     '#ffa500' // Regular orange for single cases
                            }}>
                              {count} {count === 1 ? 'case' : 'cases'}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            {count >= 6 ? 'Very High Concentration' : 
                             count >= 5 ? 'High Concentration' : 
                             count >= 4 ? 'Medium Concentration' : 
                             count >= 3 ? 'Medium-Low Concentration' : 
                             count >= 2 ? 'Low Concentration' : 'Isolated Case'}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))
                }
              </List>
            ) : (
              <Typography>No data available.</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GeospatialMap; 